import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { API } from "@/App";
import { toast } from "sonner";
import axios from "axios";
import { 
  Leaf, 
  ArrowLeft, 
  ArrowRight, 
  Check,
  User,
  Briefcase,
  Award,
  Calendar,
  Link as LinkIcon,
  FileText
} from "lucide-react";

const STEPS = [
  { id: "personal", title: "Personal Info", icon: User, description: "Tell us about yourself" },
  { id: "ministry", title: "Ministry Experience", icon: Briefcase, description: "Your ministry background" },
  { id: "specializations", title: "Specializations", icon: FileText, description: "Areas you coach" },
  { id: "certifications", title: "Certifications", icon: Award, description: "Your credentials" },
  { id: "personality", title: "Assessments", icon: User, description: "Your personality profile" },
  { id: "availability", title: "Availability", icon: Calendar, description: "When you can coach" },
  { id: "links", title: "Links & Media", icon: LinkIcon, description: "Your online presence" },
  { id: "agreement", title: "Agreement", icon: FileText, description: "Terms and liability waiver" }
];

const COACHING_SUBJECTS = [
  "Leadership Resilience",
  "Sabbatical Planning & Re-entry",
  "Church Staffing & Team Leadership",
  "Succession & Transition Planning",
  "Church Planting & Launch",
  "Spiritual Formation & Soul Care",
  "Conflict Resolution & Reconciliation",
  "Vision Casting & Strategic Planning",
  "Non-Profit Leadership & Governance",
  "Fundraising & Stewardship",
  "Multisite & Church Growth",
  "Revitalization & Turnaround",
  "Bi-vocational Ministry",
  "Cross-Cultural & Missions Leadership",
  "Board & Elder Relations"
];

const INDUSTRIES = [
  "Local Church",
  "Multi-site Church",
  "Church Plant",
  "Denominational Leadership",
  "Parachurch Ministry",
  "Christian Non-Profit",
  "Missions Organization",
  "Christian Education",
  "Seminary / Bible College",
  "Marketplace Ministry",
  "International Ministry"
];

const CERTIFICATIONS_LIST = [
  "ICF ACC (Associate Certified Coach)",
  "ICF PCC (Professional Certified Coach)",
  "ICF MCC (Master Certified Coach)",
  "Certified Life Coach",
  "Enneagram Equipped",
  "Primal Question Prepared",
  "DISC Certified",
  "Myers-Briggs Certified",
  "StrengthsFinder Certified",
  "Pastoral Counseling Certification",
  "MDiv or Equivalent",
  "DMin or Equivalent",
  "Seminary Training",
  "Church Planting Training",
  "Non-Profit Management Certification",
  "Leadership Institute",
  "Soul Shepherding",
  "LeaderBreakThru",
  "Primal Path"
];

const DAYS_OF_WEEK = [
  "Monday",
  "Tuesday", 
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday"
];

const SESSION_FORMATS = [
  "Video Call",
  "Phone Call",
  "In Person",
  "Group Sessions",
  "Workshops",
  "Email/Async"
];

const ENNEAGRAM_TYPES = [
  { value: "1", label: "Type 1 - The Reformer" },
  { value: "2", label: "Type 2 - The Helper" },
  { value: "3", label: "Type 3 - The Achiever" },
  { value: "4", label: "Type 4 - The Individualist" },
  { value: "5", label: "Type 5 - The Investigator" },
  { value: "6", label: "Type 6 - The Loyalist" },
  { value: "7", label: "Type 7 - The Enthusiast" },
  { value: "8", label: "Type 8 - The Challenger" },
  { value: "9", label: "Type 9 - The Peacemaker" }
];

const MYERS_BRIGGS_TYPES = [
  "INTJ", "INTP", "ENTJ", "ENTP",
  "INFJ", "INFP", "ENFJ", "ENFP",
  "ISTJ", "ISFJ", "ESTJ", "ESFJ",
  "ISTP", "ISFP", "ESTP", "ESFP"
];

const STRENGTHS_FINDER = [
  "Achiever", "Activator", "Adaptability", "Analytical", "Arranger",
  "Belief", "Command", "Communication", "Competition", "Connectedness",
  "Consistency", "Context", "Deliberative", "Developer", "Discipline",
  "Empathy", "Focus", "Futuristic", "Harmony", "Ideation",
  "Includer", "Individualization", "Input", "Intellection", "Learner",
  "Maximizer", "Positivity", "Relator", "Responsibility", "Restorative",
  "Self-Assurance", "Significance", "Strategic", "Woo"
];

const PRIMAL_QUESTIONS = [
  { value: "Am I safe?", label: "Am I safe?" },
  { value: "Am I loved?", label: "Am I loved?" },
  { value: "Am I enough?", label: "Am I enough?" },
  { value: "Do I belong?", label: "Do I belong?" },
  { value: "Am I in control?", label: "Am I in control?" }
];

const CoachApplicationPage = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    // Personal Info & Account
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    confirm_password: "",
    phone: "",
    gender: "",
    location: "",
    
    // Experience
    years_coaching: 0,
    total_clients_coached: "",
    coaching_background: "",
    
    // Ministry Experience
    longest_tenure_years: "",
    churches_planted: "",
    has_international_experience: false,
    has_succession_experience: false,
    has_sabbatical_experience: false,
    has_revitalization_experience: false,
    ministry_roles_held: [],
    
    // Specializations
    subjects_coached: [],
    industries_served: [],
    
    // Certifications
    certifications: [],
    other_certifications: "",
    
    // Personality Assessments
    enneagram: "",
    myers_briggs: "",
    strengths_finder: [],
    primal_question: "",
    
    // Availability
    availability_hours: "",
    availability_days: [],
    session_formats: [],
    
    // Links & Media
    photo_url: "",
    website_url: "",
    linkedin_url: "",
    articles_links: "",
    
    // Additional
    bio: "",
    why_coach: "",
    
    // Agreements
    agrees_free_consultation: false,
    agrees_liability_waiver: false,
    agrees_own_insurance: false
  });

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleArrayValue = (field, value) => {
    setFormData(prev => {
      const current = prev[field] || [];
      const updated = current.includes(value)
        ? current.filter(v => v !== value)
        : [...current, value];
      return { ...prev, [field]: updated };
    });
  };

  const handleNext = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    if (!formData.first_name || !formData.last_name || !formData.email) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (!formData.password || formData.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    if (formData.password !== formData.confirm_password) {
      toast.error("Passwords do not match");
      return;
    }

    if (!formData.agrees_free_consultation || !formData.agrees_own_insurance || !formData.agrees_liability_waiver) {
      toast.error("Please agree to all terms before submitting");
      return;
    }

    setLoading(true);
    try {
      // Remove confirm_password before sending
      const { confirm_password, ...applicationData } = formData;
      
      await axios.post(`${API}/coach-applications`, {
        application_data: applicationData
      });
      
      toast.success("Application submitted successfully! We'll be in touch soon.");
      navigate("/coach-application-success");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit application. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const progress = ((currentStep + 1) / STEPS.length) * 100;

  const renderStep = () => {
    const stepVariants = {
      enter: { x: 50, opacity: 0 },
      center: { x: 0, opacity: 1 },
      exit: { x: -50, opacity: 0 }
    };

    switch (currentStep) {
      case 0: // Personal Info
        return (
          <motion.div
            key="personal"
            variants={stepVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.3 }}
            className="space-y-6"
          >
            {/* Account Creation Notice */}
            <div className="p-4 bg-[#7C9A92]/10 rounded-xl border border-[#7C9A92]/20">
              <p className="text-sm text-[#2C332B]">
                <strong>Create Your Coach Account:</strong> You'll use this email and password to log in to your coach dashboard once your application is approved.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-[#2C332B] font-medium">First Name *</Label>
                <Input
                  placeholder="John"
                  value={formData.first_name}
                  onChange={(e) => updateFormData("first_name", e.target.value)}
                  className="h-12 rounded-xl border-stone-200 bg-stone-50/50"
                  required
                  data-testid="coach-first-name"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[#2C332B] font-medium">Last Name *</Label>
                <Input
                  placeholder="Doe"
                  value={formData.last_name}
                  onChange={(e) => updateFormData("last_name", e.target.value)}
                  className="h-12 rounded-xl border-stone-200 bg-stone-50/50"
                  required
                  data-testid="coach-last-name"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-[#2C332B] font-medium">Email *</Label>
                <Input
                  type="email"
                  placeholder="john@example.com"
                  value={formData.email}
                  onChange={(e) => updateFormData("email", e.target.value)}
                  className="h-12 rounded-xl border-stone-200 bg-stone-50/50"
                  required
                  data-testid="coach-email"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[#2C332B] font-medium">Phone</Label>
                <Input
                  type="tel"
                  placeholder="(555) 123-4567"
                  value={formData.phone}
                  onChange={(e) => updateFormData("phone", e.target.value)}
                  className="h-12 rounded-xl border-stone-200 bg-stone-50/50"
                  data-testid="coach-phone"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-[#2C332B] font-medium">Password *</Label>
                <Input
                  type="password"
                  placeholder="Min. 6 characters"
                  value={formData.password}
                  onChange={(e) => updateFormData("password", e.target.value)}
                  className="h-12 rounded-xl border-stone-200 bg-stone-50/50"
                  required
                  data-testid="coach-password"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[#2C332B] font-medium">Confirm Password *</Label>
                <Input
                  type="password"
                  placeholder="Confirm your password"
                  value={formData.confirm_password}
                  onChange={(e) => updateFormData("confirm_password", e.target.value)}
                  className="h-12 rounded-xl border-stone-200 bg-stone-50/50"
                  required
                  data-testid="coach-confirm-password"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-[#2C332B] font-medium">Gender</Label>
                <Select
                  value={formData.gender}
                  onValueChange={(v) => updateFormData("gender", v)}
                >
                  <SelectTrigger className="h-12 rounded-xl" data-testid="coach-gender-select">
                    <SelectValue placeholder="Select gender" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Male">Male</SelectItem>
                    <SelectItem value="Female">Female</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-[#2C332B] font-medium">Location</Label>
                <Input
                  placeholder="City, State"
                  value={formData.location}
                  onChange={(e) => updateFormData("location", e.target.value)}
                  className="h-12 rounded-xl border-stone-200 bg-stone-50/50"
                  data-testid="coach-location"
                />
              </div>
            </div>
          </motion.div>
        );

      case 1: // Ministry Experience
        return (
          <motion.div
            key="ministry"
            variants={stepVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.3 }}
            className="space-y-6"
          >
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-[#2C332B] font-medium">Years of Coaching Experience</Label>
                <Select
                  value={formData.years_coaching.toString()}
                  onValueChange={(v) => updateFormData("years_coaching", parseInt(v))}
                >
                  <SelectTrigger className="h-12 rounded-xl" data-testid="years-coaching-select">
                    <SelectValue placeholder="Select years" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">Less than 1 year</SelectItem>
                    <SelectItem value="1">1-2 years</SelectItem>
                    <SelectItem value="3">3-5 years</SelectItem>
                    <SelectItem value="6">6-10 years</SelectItem>
                    <SelectItem value="11">11-15 years</SelectItem>
                    <SelectItem value="16">16+ years</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-[#2C332B] font-medium">Longest Leadership Tenure</Label>
                <Select
                  value={formData.longest_tenure_years}
                  onValueChange={(v) => updateFormData("longest_tenure_years", v)}
                >
                  <SelectTrigger className="h-12 rounded-xl" data-testid="tenure-select">
                    <SelectValue placeholder="Select years" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1-3">1-3 years</SelectItem>
                    <SelectItem value="4-7">4-7 years</SelectItem>
                    <SelectItem value="8-12">8-12 years</SelectItem>
                    <SelectItem value="13-20">13-20 years</SelectItem>
                    <SelectItem value="20+">20+ years</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-4">
              <Label className="text-[#2C332B] font-medium text-lg">
                Ministry roles you've held (select all that apply)
              </Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {[
                  "Senior/Lead Pastor", "Associate Pastor", "Executive Pastor",
                  "Worship Pastor", "Youth Pastor", "Children's Pastor",
                  "Church Planter", "Missionary", "Non-Profit Director",
                  "Denominational Leader", "Seminary Professor", "Elder/Board Member"
                ].map((role) => (
                  <button
                    key={role}
                    type="button"
                    onClick={() => toggleArrayValue("ministry_roles_held", role)}
                    className={`p-3 rounded-xl border text-sm font-medium transition-all ${
                      formData.ministry_roles_held?.includes(role)
                        ? "bg-[#7C9A92] text-white border-[#7C9A92]"
                        : "bg-white text-[#2C332B] border-stone-200 hover:border-[#7C9A92]/50"
                    }`}
                  >
                    {role}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-4">
              <Label className="text-[#2C332B] font-medium text-lg">
                Ministry experience (check all that apply)
              </Label>
              <div className="space-y-3">
                {[
                  { key: "churches_planted", label: "Have you planted a church?", type: "select", options: ["No", "1 church", "2-3 churches", "4+ churches"] },
                  { key: "has_international_experience", label: "International ministry experience", type: "checkbox" },
                  { key: "has_succession_experience", label: "Navigated a pastoral succession (as predecessor or successor)", type: "checkbox" },
                  { key: "has_sabbatical_experience", label: "Taken and/or coached others through a sabbatical", type: "checkbox" },
                  { key: "has_revitalization_experience", label: "Led a church revitalization or turnaround", type: "checkbox" }
                ].map((item) => (
                  item.type === "checkbox" ? (
                    <label key={item.key} className="flex items-center gap-3 p-4 rounded-xl border border-stone-200 cursor-pointer hover:border-[#7C9A92]/50 transition-all">
                      <input
                        type="checkbox"
                        checked={formData[item.key] || false}
                        onChange={(e) => updateFormData(item.key, e.target.checked)}
                        className="w-5 h-5 rounded border-stone-300 text-[#7C9A92] focus:ring-[#7C9A92]"
                      />
                      <span className="text-[#2C332B] font-medium">{item.label}</span>
                    </label>
                  ) : (
                    <div key={item.key} className="flex items-center gap-4 p-4 rounded-xl border border-stone-200">
                      <span className="text-[#2C332B] font-medium flex-1">{item.label}</span>
                      <Select
                        value={formData[item.key] || ""}
                        onValueChange={(v) => updateFormData(item.key, v)}
                      >
                        <SelectTrigger className="w-40 h-10 rounded-lg">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          {item.options.map((opt) => (
                            <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-[#2C332B] font-medium">Tell us about your ministry and coaching background</Label>
              <Textarea
                placeholder="Describe your ministry journey, coaching experience, and what draws you to coach other ministry leaders..."
                value={formData.coaching_background}
                onChange={(e) => updateFormData("coaching_background", e.target.value)}
                className="min-h-[120px] rounded-xl border-stone-200 bg-stone-50/50"
                data-testid="coaching-background"
              />
            </div>
          </motion.div>
        );

      case 2: // Specializations
        return (
          <motion.div
            key="specializations"
            variants={stepVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.3 }}
            className="space-y-8"
          >
            <div className="space-y-4">
              <Label className="text-[#2C332B] font-medium text-lg">
                What subjects do you coach? (select all that apply)
              </Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {COACHING_SUBJECTS.map((subject) => (
                  <button
                    key={subject}
                    type="button"
                    onClick={() => toggleArrayValue("subjects_coached", subject)}
                    className={`p-3 rounded-xl border text-sm font-medium transition-all text-left ${
                      formData.subjects_coached.includes(subject)
                        ? "bg-[#7C9A92] text-white border-[#7C9A92]"
                        : "bg-white text-[#2C332B] border-stone-200 hover:border-[#7C9A92]/50"
                    }`}
                    data-testid={`subject-${subject.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {subject}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-4">
              <Label className="text-[#2C332B] font-medium text-lg">
                What industries have you served? (select all that apply)
              </Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {INDUSTRIES.map((industry) => (
                  <button
                    key={industry}
                    type="button"
                    onClick={() => toggleArrayValue("industries_served", industry)}
                    className={`p-3 rounded-xl border text-sm font-medium transition-all ${
                      formData.industries_served.includes(industry)
                        ? "bg-[#E09F7D] text-white border-[#E09F7D]"
                        : "bg-white text-[#2C332B] border-stone-200 hover:border-[#E09F7D]/50"
                    }`}
                    data-testid={`industry-${industry.toLowerCase().replace(/[\s\/]+/g, '-')}`}
                  >
                    {industry}
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        );

      case 3: // Certifications
        return (
          <motion.div
            key="certifications"
            variants={stepVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.3 }}
            className="space-y-8"
          >
            <div className="space-y-4">
              <Label className="text-[#2C332B] font-medium text-lg">
                What certifications do you hold? (select all that apply)
              </Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {CERTIFICATIONS_LIST.map((cert) => (
                  <button
                    key={cert}
                    type="button"
                    onClick={() => toggleArrayValue("certifications", cert)}
                    className={`p-4 rounded-xl border text-sm font-medium transition-all text-left ${
                      formData.certifications.includes(cert)
                        ? "bg-[#7C9A92] text-white border-[#7C9A92]"
                        : "bg-white text-[#2C332B] border-stone-200 hover:border-[#7C9A92]/50"
                    }`}
                    data-testid={`cert-${cert.toLowerCase().replace(/[\s()]+/g, '-')}`}
                  >
                    {cert}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-[#2C332B] font-medium">Other Certifications or Credentials</Label>
              <Textarea
                placeholder="List any other relevant certifications, degrees, or credentials..."
                value={formData.other_certifications}
                onChange={(e) => updateFormData("other_certifications", e.target.value)}
                className="min-h-[100px] rounded-xl border-stone-200 bg-stone-50/50"
                data-testid="other-certifications"
              />
            </div>
          </motion.div>
        );

      case 4: // Personality Assessments
        return (
          <motion.div
            key="personality"
            variants={stepVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.3 }}
            className="space-y-8"
          >
            <p className="text-[#78716C] text-sm mb-4">
              Sharing your assessment results helps us match you with compatible clients. All fields are optional but recommended.
            </p>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-[#2C332B] font-medium">Enneagram Type</Label>
                <Select
                  value={formData.enneagram}
                  onValueChange={(v) => updateFormData("enneagram", v)}
                >
                  <SelectTrigger className="h-12 rounded-xl" data-testid="coach-enneagram-select">
                    <SelectValue placeholder="Select your type" />
                  </SelectTrigger>
                  <SelectContent>
                    {ENNEAGRAM_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-[#2C332B] font-medium">Myers-Briggs Type</Label>
                <Select
                  value={formData.myers_briggs}
                  onValueChange={(v) => updateFormData("myers_briggs", v)}
                >
                  <SelectTrigger className="h-12 rounded-xl" data-testid="coach-myers-briggs-select">
                    <SelectValue placeholder="Select your type" />
                  </SelectTrigger>
                  <SelectContent>
                    {MYERS_BRIGGS_TYPES.map((type) => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-4">
              <Label className="text-[#2C332B] font-medium text-lg">
                StrengthsFinder Top 5 (select up to 5)
              </Label>
              <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
                {STRENGTHS_FINDER.map((strength) => (
                  <button
                    key={strength}
                    type="button"
                    onClick={() => {
                      const current = formData.strengths_finder || [];
                      if (current.includes(strength)) {
                        updateFormData("strengths_finder", current.filter(s => s !== strength));
                      } else if (current.length < 5) {
                        updateFormData("strengths_finder", [...current, strength]);
                      }
                    }}
                    className={`p-2 rounded-lg border text-xs font-medium transition-all ${
                      formData.strengths_finder?.includes(strength)
                        ? "bg-[#7C9A92] text-white border-[#7C9A92]"
                        : "bg-white text-[#2C332B] border-stone-200 hover:border-[#7C9A92]/50"
                    }`}
                    data-testid={`coach-strength-${strength.toLowerCase()}`}
                  >
                    {strength}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-[#2C332B] font-medium">Primal Question</Label>
              <Select
                value={formData.primal_question}
                onValueChange={(v) => updateFormData("primal_question", v)}
              >
                <SelectTrigger className="h-12 rounded-xl" data-testid="coach-primal-question-select">
                  <SelectValue placeholder="Select your primal question" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unknown">I don't know</SelectItem>
                  {PRIMAL_QUESTIONS.map((pq) => (
                    <SelectItem key={pq.value} value={pq.value}>{pq.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </motion.div>
        );

      case 5: // Availability
        return (
          <motion.div
            key="availability"
            variants={stepVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.3 }}
            className="space-y-8"
          >
            <div className="space-y-2">
              <Label className="text-[#2C332B] font-medium">Hours Available Per Week</Label>
              <Select
                value={formData.availability_hours}
                onValueChange={(v) => updateFormData("availability_hours", v)}
              >
                <SelectTrigger className="h-12 rounded-xl" data-testid="availability-hours">
                  <SelectValue placeholder="Select hours per week" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1-5">1-5 hours/week</SelectItem>
                  <SelectItem value="6-10">6-10 hours/week</SelectItem>
                  <SelectItem value="11-20">11-20 hours/week</SelectItem>
                  <SelectItem value="21-30">21-30 hours/week</SelectItem>
                  <SelectItem value="30+">30+ hours/week</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-4">
              <Label className="text-[#2C332B] font-medium text-lg">
                Which days are you typically available?
              </Label>
              <div className="flex flex-wrap gap-3">
                {DAYS_OF_WEEK.map((day) => (
                  <button
                    key={day}
                    type="button"
                    onClick={() => toggleArrayValue("availability_days", day)}
                    className={`px-5 py-3 rounded-xl border font-medium transition-all ${
                      formData.availability_days.includes(day)
                        ? "bg-[#7C9A92] text-white border-[#7C9A92]"
                        : "bg-white text-[#2C332B] border-stone-200 hover:border-[#7C9A92]/50"
                    }`}
                    data-testid={`day-${day.toLowerCase()}`}
                  >
                    {day}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-4">
              <Label className="text-[#2C332B] font-medium text-lg">
                What session formats do you offer?
              </Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {SESSION_FORMATS.map((format) => (
                  <button
                    key={format}
                    type="button"
                    onClick={() => toggleArrayValue("session_formats", format)}
                    className={`p-3 rounded-xl border text-sm font-medium transition-all ${
                      formData.session_formats.includes(format)
                        ? "bg-[#E09F7D] text-white border-[#E09F7D]"
                        : "bg-white text-[#2C332B] border-stone-200 hover:border-[#E09F7D]/50"
                    }`}
                    data-testid={`format-${format.toLowerCase().replace(/[\s\/]+/g, '-')}`}
                  >
                    {format}
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        );

      case 6: // Links & Media
        return (
          <motion.div
            key="links"
            variants={stepVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.3 }}
            className="space-y-6"
          >
            <div className="space-y-2">
              <Label className="text-[#2C332B] font-medium">Profile Photo URL</Label>
              <Input
                type="url"
                placeholder="https://example.com/your-photo.jpg"
                value={formData.photo_url}
                onChange={(e) => updateFormData("photo_url", e.target.value)}
                className="h-12 rounded-xl border-stone-200 bg-stone-50/50"
                data-testid="photo-url"
              />
              <p className="text-sm text-[#78716C]">
                Please provide a link to a professional headshot. You can upload to services like Imgur or Google Drive and paste the direct link.
              </p>
            </div>

            <div className="space-y-2">
              <Label className="text-[#2C332B] font-medium">Personal Website</Label>
              <Input
                type="url"
                placeholder="https://yourwebsite.com"
                value={formData.website_url}
                onChange={(e) => updateFormData("website_url", e.target.value)}
                className="h-12 rounded-xl border-stone-200 bg-stone-50/50"
                data-testid="website-url"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-[#2C332B] font-medium">LinkedIn Profile</Label>
              <Input
                type="url"
                placeholder="https://linkedin.com/in/yourprofile"
                value={formData.linkedin_url}
                onChange={(e) => updateFormData("linkedin_url", e.target.value)}
                className="h-12 rounded-xl border-stone-200 bg-stone-50/50"
                data-testid="linkedin-url"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-[#2C332B] font-medium">Articles, Publications, or Media Features</Label>
              <Textarea
                placeholder="Share links to any articles you've written, podcasts you've been on, or media features..."
                value={formData.articles_links}
                onChange={(e) => updateFormData("articles_links", e.target.value)}
                className="min-h-[100px] rounded-xl border-stone-200 bg-stone-50/50"
                data-testid="articles-links"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-[#2C332B] font-medium">Short Bio (for your profile)</Label>
              <Textarea
                placeholder="Write a brief bio that will be displayed on your coach profile..."
                value={formData.bio}
                onChange={(e) => updateFormData("bio", e.target.value)}
                className="min-h-[120px] rounded-xl border-stone-200 bg-stone-50/50"
                data-testid="coach-bio"
              />
            </div>
          </motion.div>
        );

      case 7: // Agreement
        return (
          <motion.div
            key="agreement"
            variants={stepVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.3 }}
            className="space-y-6"
          >
            <div className="p-4 bg-[#F5F1E8] rounded-xl">
              <p className="text-sm text-[#2C332B]">
                Please review and agree to the following terms before submitting your application.
              </p>
            </div>

            {/* Free Consultation Agreement */}
            <div className="p-5 border border-stone-200 rounded-xl space-y-3">
              <h3 className="font-semibold text-[#2C332B]">Free Consultation Commitment</h3>
              <p className="text-sm text-[#78716C]">
                As a CoachBridge approved coach, I agree to provide a <strong>FREE 15-minute phone or video consultation</strong> to each potential client who connects with me through the platform. This consultation allows both parties to determine if they are a good match before moving forward with paid coaching sessions.
              </p>
              <label className="flex items-start gap-3 cursor-pointer mt-4">
                <input
                  type="checkbox"
                  checked={formData.agrees_free_consultation}
                  onChange={(e) => updateFormData("agrees_free_consultation", e.target.checked)}
                  className="w-5 h-5 mt-0.5 rounded border-stone-300 text-[#7C9A92] focus:ring-[#7C9A92]"
                  data-testid="agree-consultation"
                />
                <span className="text-sm text-[#2C332B]">
                  I agree to provide a free 15-minute consultation to potential clients.
                </span>
              </label>
            </div>

            {/* Insurance Requirement */}
            <div className="p-5 border border-stone-200 rounded-xl space-y-3">
              <h3 className="font-semibold text-[#2C332B]">Insurance Requirement</h3>
              <p className="text-sm text-[#78716C]">
                All coaches on CoachBridge are required to carry their own professional liability insurance. CoachBridge does not provide insurance coverage for coaching sessions. You are responsible for maintaining appropriate coverage for your coaching practice.
              </p>
              <label className="flex items-start gap-3 cursor-pointer mt-4">
                <input
                  type="checkbox"
                  checked={formData.agrees_own_insurance}
                  onChange={(e) => updateFormData("agrees_own_insurance", e.target.checked)}
                  className="w-5 h-5 mt-0.5 rounded border-stone-300 text-[#7C9A92] focus:ring-[#7C9A92]"
                  data-testid="agree-insurance"
                />
                <span className="text-sm text-[#2C332B]">
                  I confirm that I carry my own professional liability insurance and will maintain appropriate coverage.
                </span>
              </label>
            </div>

            {/* Liability Waiver */}
            <div className="p-5 border border-stone-200 rounded-xl space-y-3">
              <h3 className="font-semibold text-[#2C332B]">Liability Release & Waiver</h3>
              <div className="text-sm text-[#78716C] space-y-2 max-h-48 overflow-y-auto p-3 bg-stone-50 rounded-lg">
                <p>
                  By checking the box below, I acknowledge and agree to the following:
                </p>
                <p>
                  <strong>RELEASE OF LIABILITY:</strong> I hereby release, waive, discharge, and covenant not to sue CoachBridge, its officers, directors, employees, agents, and affiliates from any and all liability, claims, demands, actions, or causes of action whatsoever arising out of or related to any loss, damage, or injury that may be sustained by me or any third party, arising from or in connection with my use of the CoachBridge platform or any coaching services provided through or facilitated by the platform.
                </p>
                <p>
                  <strong>INDEPENDENT CONTRACTOR STATUS:</strong> I understand and agree that I am an independent contractor and not an employee, agent, or representative of CoachBridge. I am solely responsible for my coaching practice, including but not limited to: setting my own rates, collecting payments directly from clients, managing my own schedule, and complying with all applicable laws and regulations.
                </p>
                <p>
                  <strong>INDEMNIFICATION:</strong> I agree to indemnify, defend, and hold harmless CoachBridge from and against any and all claims, damages, losses, costs, and expenses (including reasonable attorneys' fees) arising out of or relating to my coaching services, my breach of this agreement, or my violation of any law or the rights of any third party.
                </p>
                <p>
                  <strong>PAYMENT HANDLING:</strong> I understand that CoachBridge does not process payments for coaching services. All payment arrangements are made directly between myself and my clients. I am solely responsible for invoicing, collecting payment, and handling any payment disputes.
                </p>
              </div>
              <label className="flex items-start gap-3 cursor-pointer mt-4">
                <input
                  type="checkbox"
                  checked={formData.agrees_liability_waiver}
                  onChange={(e) => updateFormData("agrees_liability_waiver", e.target.checked)}
                  className="w-5 h-5 mt-0.5 rounded border-stone-300 text-[#7C9A92] focus:ring-[#7C9A92]"
                  data-testid="agree-waiver"
                />
                <span className="text-sm text-[#2C332B]">
                  I have read, understand, and agree to the Liability Release & Waiver terms above.
                </span>
              </label>
            </div>
          </motion.div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Leaf className="w-6 h-6 text-[#7C9A92]" />
            <span className="font-serif text-xl font-medium text-[#2C332B]">CoachBridge</span>
          </Link>
          <span className="text-[#78716C] font-medium">Coach Application</span>
        </div>
      </header>

      {/* Progress Bar */}
      <div className="sticky top-[72px] z-40 bg-[#FAFAF9] py-2">
        <div className="max-w-4xl mx-auto px-6">
          <Progress value={progress} className="h-2 bg-stone-200" />
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-6 py-12">
        {/* Step Indicator */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {STEPS.map((step, index) => (
            <div
              key={step.id}
              className={`w-3 h-3 rounded-full transition-all ${
                index === currentStep
                  ? "bg-[#7C9A92] scale-125"
                  : index < currentStep
                  ? "bg-[#7C9A92]"
                  : "bg-stone-200"
              }`}
            />
          ))}
        </div>

        {/* Step Title */}
        <motion.div
          key={`title-${currentStep}`}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-10"
        >
          <div className="w-16 h-16 rounded-2xl bg-[#7C9A92]/10 flex items-center justify-center mx-auto mb-4">
            {(() => {
              const Icon = STEPS[currentStep].icon;
              return <Icon className="w-8 h-8 text-[#7C9A92]" />;
            })()}
          </div>
          <h1 className="font-serif text-3xl md:text-4xl font-light text-[#2C332B] mb-2">
            {STEPS[currentStep].title}
          </h1>
          <p className="text-[#78716C]">{STEPS[currentStep].description}</p>
        </motion.div>

        {/* Form Content */}
        <div className="bg-white rounded-2xl p-8 border border-stone-100 shadow-sm min-h-[400px]">
          <AnimatePresence mode="wait">
            {renderStep()}
          </AnimatePresence>
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-8">
          <Button
            variant="outline"
            onClick={handlePrev}
            disabled={currentStep === 0}
            className="rounded-full px-6 py-5 border-stone-200"
            data-testid="prev-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Previous
          </Button>

          {currentStep === STEPS.length - 1 ? (
            <Button
              onClick={handleSubmit}
              disabled={loading}
              className="rounded-full px-8 py-5 bg-[#7C9A92] hover:bg-[#6B8A82] text-white"
              data-testid="submit-application-btn"
            >
              {loading ? "Submitting..." : "Submit Application"}
              <Check className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleNext}
              className="rounded-full px-8 py-5 bg-[#7C9A92] hover:bg-[#6B8A82] text-white"
              data-testid="next-btn"
            >
              Next
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
      </main>
    </div>
  );
};

export default CoachApplicationPage;
